import {useState, useEffect, useEffectEvent, useRef, useCallback} from 'react'
import type {CLIAgentMode} from '../components/task-view/CLIModePicker'
import {isSessionModeChangedMessage, isSessionResumeMessage} from '../types/session-events'
import {useLogsQuery} from './useLogs'

const CLI_AGENT_MODES: readonly string[] = ['interactive', 'plan', 'autopilot']

function isValidCLIAgentMode(mode: string): mode is CLIAgentMode {
  return CLI_AGENT_MODES.includes(mode)
}

interface UseCLIModeSyncOptions {
  sessionId: string
}

interface UseCLIModeSyncResult {
  /** The mode that the dropdown should display */
  agentMode: CLIAgentMode
  /** Whether a mode change is in-flight. The picker stays enabled so users can queue another change. */
  isPending: boolean
  /** Call when the user picks a new mode from the dropdown. Returns the previous mode for snapshot rollback. */
  handleModeChange: (mode: CLIAgentMode) => {previousMode: CLIAgentMode}
  /** Call when the mode_switch command fails to roll back the optimistic update */
  handleModeChangeError: (previousMode: CLIAgentMode) => void
}

/**
 * Manages CLI agent mode state and keeps it in sync with mode change events from the log stream.
 *
 * Implements pending-mode reconciliation:
 * - When the user picks a mode, we optimistically update the UI and track a `pendingMode`.
 * - When a `session.mode_changed` log event arrives that matches `pendingMode`, we clear it.
 * - If the log reports an unexpected mode, we snap the UI to whatever the CLI says.
 */
export function useCLIModeSync({sessionId}: UseCLIModeSyncOptions): UseCLIModeSyncResult {
  const [agentMode, setAgentMode] = useState<CLIAgentMode>('interactive')
  const [pendingMode, setPendingMode] = useState<CLIAgentMode | null>(null)
  const lastSeenModeTimestampRef = useRef<string | null>(null)

  const {data: logs} = useLogsQuery({sessionId})

  // Reconcile UI state with the latest mode from the log stream.
  // useEffectEvent so we read pendingMode/agentMode fresh without re-triggering
  // the effect when only those values change (e.g. after an optimistic update).
  const reconcileModeFromLogs = useEffectEvent((logEntries: typeof logs) => {
    if (!logEntries || logEntries.length === 0) return

    let latestLogMode: CLIAgentMode | null = null
    let latestTimestamp: string | null = null
    let isResumeEvent = false
    for (let i = logEntries.length - 1; i >= 0; i--) {
      const entry = logEntries[i]
      if (entry && isSessionModeChangedMessage(entry) && isValidCLIAgentMode(entry.data.newMode)) {
        latestLogMode = entry.data.newMode
        latestTimestamp = entry.timestamp
        break
      }
      // session.resume means the CLI restarted — it always starts in interactive mode
      if (entry && isSessionResumeMessage(entry)) {
        latestLogMode = 'interactive'
        latestTimestamp = entry.timestamp
        isResumeEvent = true
        break
      }
    }

    if (!latestLogMode || !latestTimestamp) return

    // Skip if we've already processed this event
    if (lastSeenModeTimestampRef.current !== null && latestTimestamp <= lastSeenModeTimestampRef.current) return
    lastSeenModeTimestampRef.current = latestTimestamp

    // session.resume force-resets to interactive regardless of pending state,
    // because the CLI restarted and any in-flight commands are lost.
    if (isResumeEvent) {
      setAgentMode('interactive')
      setPendingMode(null)
      return
    }

    if (pendingMode !== null) {
      if (latestLogMode === pendingMode) {
        setPendingMode(null)
      } else if (latestLogMode !== agentMode) {
        setAgentMode(latestLogMode)
        setPendingMode(null)
      }
    } else {
      if (latestLogMode !== agentMode) {
        setAgentMode(latestLogMode)
      }
    }
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    reconcileModeFromLogs(logs)
  }, [logs])

  const handleModeChange = useCallback(
    (mode: CLIAgentMode) => {
      const previousMode = agentMode
      setAgentMode(mode)
      setPendingMode(mode)
      return {previousMode}
    },
    [agentMode],
  )

  const handleModeChangeError = useCallback((previousMode: CLIAgentMode) => {
    setAgentMode(previousMode)
    setPendingMode(null)
  }, [])

  return {agentMode, isPending: pendingMode !== null, handleModeChange, handleModeChangeError}
}
